import { useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationBarProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const PAGE_SIZES = [10, 25, 50];

export default function PaginationBar({ total, page, pageSize, onPageChange, onPageSizeChange }: PaginationBarProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);

  // A delete can leave `page` past the end (and hide this bar once total <= 10),
  // stranding the list on an empty page. Step back to the last real page.
  useEffect(() => {
    if (page > 0 && page >= totalPages) onPageChange(totalPages - 1);
  }, [page, totalPages, onPageChange]);

  if (total <= PAGE_SIZES[0]) return null;

  return (
    <div className="flex items-center justify-between py-3 text-[11px] text-slate-500">
      <div className="flex items-center gap-2">
        <span className="text-slate-400">Show</span>
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
          {PAGE_SIZES.map(s => (
            <button
              key={s}
              onClick={() => onPageSizeChange(s)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                pageSize === s ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="tabular-nums">{total > 0 ? `${from}–${to} of ${total}` : "0 results"}</span>
        <div className={`flex items-center gap-0.5 ${totalPages <= 1 ? "opacity-30 pointer-events-none" : ""}`}>
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="p-1 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="size-3" />
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            className="p-1 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="size-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
